import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { timelineApi } from '../utils/timelineApi';

const SettingsContainer = styled.div`
  width: 100%;
  color: white;
`;

const SettingsDescription = styled.p`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
`;

const SettingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const SettingItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
  min-width: 0;
  padding-right: 16px;
`;

const SettingTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: white;
  margin-bottom: 4px;
`;

const SettingSubtitle = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
`;

const ToggleSwitch = styled.button`
  position: relative;
  width: 44px;
  height: 26px;
  background: ${props => props.active ? '#FF2300' : 'rgba(255, 255, 255, 0.2)'};
  border: none;
  border-radius: 13px;
  cursor: pointer;
  transition: background 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.8;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.active ? '20px' : '2px'};
    width: 22px;
    height: 22px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s ease;
  }
`;

const TimelineSettings = ({ settings: controlledSettings, onSettingChange, onClose }) => {
  const [internalSettings, setInternalSettings] = useState({
    upcomingShows: true,
    upcomingReleases: true,
    tasksDeadlines: true,
    flights: true,
    hotels: true,
    newShowOffers: true,
    ticketCountUpdates: true,
    releaseWorkflowTasks: true
  });
  
  // Use controlled settings if provided, otherwise use internal state
  const settings = controlledSettings || internalSettings;
  
  useEffect(() => {
    // Load saved settings if not controlled
    if (!controlledSettings) {
      const savedSettings = timelineApi.getSettings();
      setInternalSettings(savedSettings);
    }
  }, [controlledSettings]);
  
  const toggleSetting = (key) => {
    const newValue = !settings[key];
    
    if (onSettingChange) {
      // Controlled component - call parent handler
      onSettingChange(key, newValue);
    } else {
      // Uncontrolled - update internal state
      const newSettings = {
        ...settings,
        [key]: newValue
      };
      setInternalSettings(newSettings);
      timelineApi.saveSettings(newSettings);
    }
  };
  
  const settingItems = [
    {
      key: 'upcomingShows',
      title: 'Upcoming Shows (Confirmed)',
      subtitle: 'From Bookings app, Bandsintown, Songkick, Gmail'
    },
    {
      key: 'upcomingReleases',
      title: 'Upcoming Releases',
      subtitle: 'From Catalog, Spotify for Artists, Apple Music for Artists, Cm'
    },
    {
      key: 'tasksDeadlines',
      title: 'Tasks & Deadlines',
      subtitle: 'From Tasks/Flows app'
    },
    {
      key: 'flights',
      title: 'Flights',
      subtitle: 'From TourManager, Gmail'
    },
    {
      key: 'hotels',
      title: 'Hotels',
      subtitle: 'From TourManager, Gmail'
    },
    {
      key: 'newShowOffers',
      title: 'New Show Offers',
      subtitle: 'From Gmail, Bookings app'
    },
    {
      key: 'ticketCountUpdates',
      title: 'Ticket Count Updates',
      subtitle: 'From Bookings app, Promoter emails'
    },
    {
      key: 'releaseWorkflowTasks',
      title: 'Release Workflow Tasks',
      subtitle: 'From Catalog - Release Flow'
    }
  ];
  
  return (
    <SettingsContainer>
      <SettingsDescription style={{ marginBottom: '24px' }}>
        Choose the items to display in your Timeline.
      </SettingsDescription>
      
      <SettingsList>
        {settingItems.map((item) => (
          <SettingItem key={item.key}>
            <SettingInfo>
              <SettingTitle>{item.title}</SettingTitle>
              <SettingSubtitle>{item.subtitle}</SettingSubtitle>
            </SettingInfo>
            <ToggleSwitch
              active={settings[item.key]}
              onClick={() => toggleSetting(item.key)}
              aria-label={`Toggle ${item.title}`}
            />
          </SettingItem>
        ))}
      </SettingsList>
    </SettingsContainer>
  );
};

export default TimelineSettings;

